import type { FC } from "react";
import { createElement, memo } from "react";

import Typography from "@/design-system/Typography";
import Button from "@/design-system/button";
import { sendTelemetryEvent } from "@/lib/telemetry";
import type { IImageLoader } from "@Components/ImageLoader";
import ImageLoader from "@Components/ImageLoader";
import { type TelemetryClickFromType } from "@Components/Modules/Helpers";
import { discountTypeStyles } from "@Components/discount-tile/discount-tile-v3";
import TelemetryHoc from "@Components/telemetry-hoc/telemetry-hoc";
import * as stylex from "@stylexjs/stylex";

import {
    ComposableSpacing,
    type PropsWithStyleX,
    type QueryMdMax,
    type QuerySmMax,
    type QueryXsMax,
    colors,
    genericStyles,
    globalLimitStyles,
} from "../../styles/tokens.stylex";
import type { ISpecialCashback } from "../cashback/CashbackCommonInterfaces";
import CashbackLabel from "../cashback/CashbackLabel";
import LikeLink from "../common/links/LikeLink";
import { InfoLabel } from "../shared/InfoLabel";

export interface ITitleElement {
    div: HTMLDivElement;
    h4: HTMLHeadElement;
}

export interface IDescriptionElement {
    h3: HTMLHeadElement;
    h4: HTMLHeadElement;
}

export enum DiscountTileType {
    Unknown,
    Discount,
    Sale,
    NewDiscount,
    Moment,
    Flyer,
}

export interface IDiscountTile extends PropsWithStyleX {
    id: string;
    image: string;
    imageAlt: string;
    key?: number | string;
    type?: DiscountTileType;
    brandId: string;
    descriptionElement: keyof IDescriptionElement;
    typeDescription?: string;
    title?: string;
    description?: string;
    validityDate?: string;
    availability?: AvailabilityType;
    brandImage?: string;
    brandAlt?: string;
    navigateUrl?: string;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>, tileId: string) => boolean | void;
    trackerAction?: string;
    saveUrl?: string;
    isSaved?: boolean;
    likesCount: number;
    distanceDescription?: string;
    loyaltyProgramIcon?: IImageLoader;
    isNewFlyerDescription?: string;
    isLoadingLayout?: boolean;
    cashbackLabel?: string;
    isOpenUrlInstant?: boolean;
    origin?: TelemetryClickFromType;
    specialCashback?: ISpecialCashback;
    maxCashback?: string;
}

export enum AvailabilityType {
    Undefined,
    Offline,
    Online,
    Both,
}

const arePropsEqual = (prevProps: IDiscountTile, nextProps: IDiscountTile) =>
    prevProps.isSaved === nextProps.isSaved;

const DiscountTile: FC<IDiscountTile> = memo((props: IDiscountTile) => {
    const eventNamePrefix = props.type === DiscountTileType.Flyer ? "Flyer" : "Discount";

    if (props.isLoadingLayout === true) {
        return <div {...stylex.props(styles.discountTileComponent, styles.loadingLayout)} />;
    }

    const onClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (props.onClick) {
            props.onClick(event, props.id);
        }

        if (props.origin && props.origin !== "SearchResult") {
            sendTelemetryEvent({
                eventName: eventNamePrefix + "Click",
                itemId: props.id,
                origin: props.origin,
                actionType: "Click",
            });
        }
    };

    const renderValidityDate = () =>
        props.validityDate &&
        createElement(
            props.descriptionElement || "h4",
            {
                className: stylex.props(styles.discountDescriptionValidityDate, styles.spacer).className,
            },
            props.validityDate,
        );

    const renderLabels = () => {
        if (props.specialCashback) {
            return (
                <InfoLabel
                    iconType="Cashback"
                    labelType="magenta"
                    specialCashback={props.specialCashback}
                    text={props.maxCashback}
                    styleX={styles.spacer}
                />
            );
        }

        if (props.cashbackLabel) {
            return <CashbackLabel value={props.cashbackLabel} styleX={styles.spacer} />;
        }

        return null;
    };

    return (
        <Button
            as="a"
            size="None"
            styleX={[styles.discountTileComponent]}
            title={props.title}
            href={props.navigateUrl}
            onClick={onClick}
            target={props.isOpenUrlInstant ? "_blank" : "_self"}
        >
            <div {...stylex.props(styles.imageContainer)}>
                <ImageLoader
                    src={props.image}
                    alt={props.imageAlt}
                    styleX={styles.discountImage}
                    srcSetAxis="width"
                    sizeMobile={600}
                    sizeTablet={800}
                    sizeLaptop={480}
                    size={533}
                />
                {props.typeDescription && (
                    <Typography textElement="p" styleX={[styles.discountType, discountTypeCss(props.type)]}>
                        {props.typeDescription}
                    </Typography>
                )}
                {props.type !== DiscountTileType.Flyer && (
                    <LikeLink
                        id={props.id}
                        likesCount={props.likesCount}
                        isSaved={props.isSaved || false}
                        styleX={styles.likeLink}
                    />
                )}
            </div>
            <TelemetryHoc
                telemetryCustom={{
                    eventName: eventNamePrefix + "View",
                    itemId: props.id,
                    origin: props.origin,
                    actionType: "Undefined",
                }}
                options={{ rootMargin: "100px 0px 0px", triggerOnce: true }}
                styleX={styles.discountDetails}
            >
                <div {...stylex.props(styles.discountDescriptionContainer)}>
                    <h3 {...stylex.props(styles.discountDescriptionH3)}>
                        {props.title && (
                            <div
                                {...stylex.props(
                                    styles.discountDescriptionTitle,
                                    globalLimitStyles.TextTwoLinesCap,
                                )}
                            >
                                {props.title}
                            </div>
                        )}
                        {props.description && (
                            <div
                                {...stylex.props(
                                    styles.discountDescription,
                                    globalLimitStyles.TextTwoLinesCap,
                                )}
                            >
                                {props.description}
                            </div>
                        )}
                        {props.isNewFlyerDescription && (
                            <span {...stylex.props(styles.isNewDescription)}>
                                {props.isNewFlyerDescription}
                            </span>
                        )}
                    </h3>
                    {props.loyaltyProgramIcon && (
                        <ImageLoader
                            {...props.loyaltyProgramIcon}
                            styleX={[styles.loyaltyProgramIcon, styles.spacer]}
                            srcSetAxis="height"
                            size={300}
                            lazyLoading
                        />
                    )}
                    {renderLabels()}
                    {renderValidityDate()}
                </div>
                {props.brandImage && (
                    <ImageLoader
                        src={props.brandImage}
                        alt={props.brandAlt}
                        styleX={styles.brandImage}
                        srcSetAxis="width"
                        size={300}
                        lazyLoading
                    />
                )}
            </TelemetryHoc>
        </Button>
    );
}, arePropsEqual);

const mdMax: QueryMdMax = "@media (max-width: 1239px)";
const smMax: QuerySmMax = "@media (max-width: 989px)";
const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    discountTileComponent: {
        borderColor: colors.b_centas,
        borderRadius: genericStyles.borderRadiusS1,
        borderStyle: "solid",
        borderWidth: 1,
        overflow: "hidden",
        color: "inherit",
        display: "flex",
        flexDirection: "column",
        fontSize: "0.875rem",
        position: "relative",
        minHeight: {
            default: "330px",
            [xsMax]: "250px",
        },
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
    },

    loadingLayout: {
        backgroundColor: colors.b_centas,
    },

    discountType: {
        alignItems: "center",
        color: colors.b_Emphasis,
        display: "flex",
        fontSize: { default: null, [xsMax]: "0.6625rem" },
        position: "absolute",
        textAlign: "center",
        textTransform: "uppercase",
        zIndex: 1,
        borderBottomColor: colors.fg_AltDefault,
        borderBottomLeftRadius: "0",
        borderBottomRightRadius: "3px",
        borderBottomStyle: "solid",
        borderBottomWidth: 4,
        borderRightColor: colors.fg_AltDefault,

        borderRightStyle: "solid",
        borderRightWidth: 4,
        borderTopLeftRadius: "0",
        borderTopRightRadius: "0",
        height: "1.5rem",
        left: 0,
        minHeight: 20,
        paddingBottom: 0,
        paddingLeft: { default: ComposableSpacing.s4, [xsMax]: ComposableSpacing.s6 },
        paddingRight: { default: ComposableSpacing.s4, [xsMax]: ComposableSpacing.s6 },
        paddingTop: 0,
        top: 0,
    },

    discountImage: {
        aspectRatio: "2/1",
        contain: "strict",
        objectFit: "cover",
        width: "100%",
    },

    imageContainer: {
        position: "relative",
    },

    discountDetails: {
        alignItems: "center",
        display: "flex",
    },

    discountDescriptionContainer: {
        flex: "1",
        flexDirection: "column",
        paddingBottom: { default: ComposableSpacing.s4, [mdMax]: ComposableSpacing.s2 },
        paddingLeft: { default: ComposableSpacing.s4, [mdMax]: ComposableSpacing.s3 },
        paddingRight: 0,
        paddingTop: { default: ComposableSpacing.s4, [mdMax]: ComposableSpacing.s2 },
        width: "70%",
    },

    spacer: {
        margin: 0,
        marginTop: { default: ComposableSpacing.s2, [xsMax]: ComposableSpacing.s1 },
    },

    discountDescriptionH3: {
        margin: 0,
        gap: { default: ComposableSpacing.s2, [xsMax]: ComposableSpacing.s1 },
        alignItems: "start",
        display: "flex",
        flexDirection: "column",
    },

    discountDescriptionTitle: {
        fontSize: { default: "1.75rem", [smMax]: "2rem", [xsMax]: "1rem" },
        fontWeight: 300,
        lineHeight: 1.2,
        textTransform: "none",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
    },

    discountDescription: {
        fontSize: { default: "1.125rem", [smMax]: "1.25rem", [xsMax]: "0.75rem" },
        fontWeight: 300,
        lineHeight: 1.33,
        textTransform: "none",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
    },

    discountDescriptionValidityDate: {
        margin: 0,
        color: colors.grayLight,
        fontSize: { default: "1.125rem", [smMax]: "1.25rem", [xsMax]: "0.75rem" },
        fontWeight: 300,
        lineHeight: 1.333,
        textTransform: "none",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
    },

    isNewDescription: {
        borderRadius: genericStyles.borderRadiusS1,
        paddingBlock: "3px",
        paddingInline: "10px",
        backgroundColor: colors.bg_Success,
        color: "white",
        display: "inline-block",
        fontSize: { default: "0.875rem", [mdMax]: "0.75rem", [xsMax]: "0.625rem" },
        fontWeight: 400,
        lineHeight: "initial",
        textTransform: "uppercase",
        marginBottom: 0,
    },

    brandImage: {
        aspectRatio: "1/1",
        textAlign: "center",
        paddingBottom: { default: ComposableSpacing.s4, [xsMax]: ComposableSpacing.s3 },
        paddingLeft: { default: ComposableSpacing.s4, [xsMax]: ComposableSpacing.s3 },
        paddingRight: { default: ComposableSpacing.s4, [xsMax]: ComposableSpacing.s3 },
        paddingTop: { default: ComposableSpacing.s4, [xsMax]: ComposableSpacing.s3 },
        width: "30%",
    },

    likeLink: {
        position: "absolute",
        bottom: ComposableSpacing.s3,
        right: { default: ComposableSpacing.s3, [xsMax]: ComposableSpacing.s2 },
    },

    loyaltyProgramIcon: {
        borderRadius: genericStyles.borderRadiusS1,
        overflow: "hidden",
        display: "block",
        height: { default: "20px", [xsMax]: "15px" },
        maxWidth: "200px",
    },
});

const discountTypeCss = (tileType?: DiscountTileType) => {
    switch (tileType) {
        case DiscountTileType.Discount:
            return discountTypeStyles.discount;
        case DiscountTileType.Sale:
            return discountTypeStyles.sale;
        case DiscountTileType.NewDiscount:
            return discountTypeStyles.newDiscount;
        case DiscountTileType.Flyer:
            return discountTypeStyles.flyer;
        case DiscountTileType.Moment:
            return discountTypeStyles.moment;
        default:
            return undefined;
    }
};

export default DiscountTile;
